/**
 * Response service for Hugo_Responses Firestore collection.
 *
 * Document structure:
 * - surveyId: string
 * - organizationId: string
 * - respondentUid: string (uid, or null if anonymous)
 * - respondentScope: map { siteId, departmentId, teamId }
 * - answers: map { questionKey: number (1-10) }
 * - repsScores: map { reliability, efficiency, productivity, safety }
 * - completedAt: Timestamp
 */
import { db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { calculateResponseREPS } from '../assistants/drew/utils/scoring';
import { refreshSnapshotsForResponse } from './oeSnapshotService';
import { logActivity } from '../utils/firestore';
import { guardWrite, actingStamp } from './actingContext';

const COLLECTION_NAME = 'Hugo_Responses';
const DRAFT_COLLECTION_NAME = 'Hugo_DraftResponses';

/**
 * Submit a survey response.
 * Calculates REPS scores and stores them with the response.
 */
/** Has this person already submitted a response to the survey? (uses the surveyId + respondentUid pair) */
export async function hasResponded(surveyId, respondentUid) {
  if (!surveyId || !respondentUid) return false;
  const q = query(collection(db, COLLECTION_NAME), where('surveyId', '==', surveyId), where('respondentUid', '==', respondentUid), limit(1));
  const snap = await getDocs(q);
  return !snap.empty;
}

/**
 * One response per person: on unless the organization turned it off. Sandboxes (org ids starting
 * with `org_magnum_`) default to off so testers can repeat surveys and watch the numbers move.
 */
export function onePerPersonFor(org) {
  if (!org) return false;
  const explicit = org?.surveys?.oneResponsePerPerson;
  if (typeof explicit === 'boolean') return explicit;
  return !String(org.id || '').startsWith('org_magnum_');
}

export async function submitResponse({
  surveyId,
  organizationId,
  respondentUid,
  respondentScope,
  answers,
  onePerPerson = false,
}) {
  if (!surveyId) throw new Error('surveyId is required');
  if (!organizationId) throw new Error('organizationId is required');
  if (!respondentScope) throw new Error('respondentScope is required');
  if (!answers || Object.keys(answers).length === 0) throw new Error('answers are required');

  // View As (P4-06/07): blocked in read-only mode; otherwise audited for the member.
  guardWrite('survey_response', { summary: 'submitted a survey response', organizationId, surveyId });

  // Tim 26 Oct (A17 / Q-22): one response per person per survey when the organization says so
  // (default ON for real organizations; the Magnum sandboxes allow repeats so testers can play).
  if (respondentUid && onePerPerson) {
    const prior = await hasResponded(surveyId, respondentUid);
    if (prior) throw new Error('You have already responded to this survey. Each person can respond once.');
  }

  // Calculate REPS scores for this response
  const repsScores = calculateResponseREPS(answers);

  const responseData = {
    surveyId,
    organizationId,
    respondentUid: respondentUid || null,
    respondentScope,
    // Flattened for scope filtering/aggregation (scoring.filterResponsesByScope
    // reads top-level siteId/departmentId/teamId).
    siteId: respondentScope.siteId || null,
    departmentId: respondentScope.departmentId || null,
    teamId: respondentScope.teamId || null,
    answers,
    repsScores,
    isComplete: true,
    completedAt: Timestamp.now(),
    ...actingStamp(),
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), responseData);

  // Onboarding: a submitted survey = "move the numbers yourself".
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('repsit-onboarding-step', { detail: { step: 'move-the-numbers' } })
      );
    }
  } catch { /* never block a submission on onboarding */ }

  // Refresh dashboard snapshots so graphs and warnings move the moment a
  // response lands. Fire-and-forget: never blocks the respondent.
  refreshSnapshotsForResponse({ organizationId, surveyId, respondentScope }).catch((err) => {
    console.error('Snapshot refresh after response failed (non-blocking):', err);
  });

  // Log survey completion activity
  logActivity(respondentUid || 'anonymous', 'survey_completed', 'drift-accumulation', {
    surveyId,
    organizationId,
    responseId: docRef.id,
    questionCount: Object.keys(answers).length,
  });

  return { id: docRef.id, ...responseData };
}

/**
 * Get all responses for a survey
 */
export async function getResponsesForSurvey(surveyId) {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('surveyId', '==', surveyId),
    orderBy('completedAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Get responses for a survey filtered by scope
 */
export async function getResponsesByScope(surveyId, scopeField, scopeValue) {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('surveyId', '==', surveyId),
    where(`respondentScope.${scopeField}`, '==', scopeValue),
    orderBy('completedAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Check if user already responded to a survey
 */
export async function hasUserResponded(surveyId, userId) {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('surveyId', '==', surveyId),
    where('respondentUid', '==', userId)
  );
  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

/**
 * Subscribe to responses for a survey in real-time
 */
export function subscribeToResponses(surveyId, callback) {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('surveyId', '==', surveyId),
    orderBy('completedAt', 'desc')
  );
  return onSnapshot(q, (snapshot) => {
    const responses = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(responses);
  });
}

// --- Draft Responses (Auto-Save) ---

/**
 * Generate a deterministic draft ID from surveyId + userId
 */
function getDraftId(surveyId, userId) {
  return `${surveyId}_${userId}`;
}

/**
 * Save or update a draft response (auto-save)
 */
export async function saveDraftResponse({
  surveyId,
  organizationId,
  respondentUid,
  respondentScope,
  answers,
  currentStep,
}) {
  if (!surveyId || !respondentUid) return null;

  const draftId = getDraftId(surveyId, respondentUid);
  const draftData = {
    surveyId,
    organizationId: organizationId || null,
    respondentUid,
    respondentScope: respondentScope || null,
    answers: answers || {},
    currentStep: currentStep || 0,
    updatedAt: Timestamp.now(),
  };

  await setDoc(doc(db, DRAFT_COLLECTION_NAME, draftId), draftData);
  return { id: draftId, ...draftData };
}

/**
 * Get a draft response for a survey + user
 */
export async function getDraftResponse(surveyId, userId) {
  if (!surveyId || !userId) return null;

  const draftId = getDraftId(surveyId, userId);
  const docRef = doc(db, DRAFT_COLLECTION_NAME, draftId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() };
}

/**
 * Delete a draft response (after final submission)
 */
export async function deleteDraftResponse(surveyId, userId) {
  if (!surveyId || !userId) return;

  const draftId = getDraftId(surveyId, userId);
  await deleteDoc(doc(db, DRAFT_COLLECTION_NAME, draftId));
}

export { COLLECTION_NAME, DRAFT_COLLECTION_NAME };
